import React, { useEffect, useMemo, useRef, useState } from "react";
import { AutoComplete, Form } from "antd";
import { faX } from "@fortawesome/pro-regular-svg-icons";
import { createUseStyles } from "react-jss";
import Icon from "../../Icon";

const normalizeSkills = (input) => {
  if (!input) return [];

  if (Array.isArray(input)) {
    return input
      .map((item) => {
        if (typeof item === "string") {
          return {
            SkillId: item,
            Skill: item,
            SkillCode: item,
            type: "custom",
          };
        }

        if (item?.Skill) {
          return item;
        }

        return null;
      })
      .filter(Boolean);
  }

  if (typeof input === "string" && input.trim()) {
    return input
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((item) => ({
        SkillId: item,
        Skill: item,
        SkillCode: item,
        type: "custom",
      }));
  }

  return [];
};

const dedupeSkills = (list = []) => {
  const seen = new Set();

  return list.filter((item) => {
    const key = item?.Skill?.trim?.().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getRequiredMessage = (rules) => {
  const message = Array.isArray(rules) ? rules[0]?.message : rules?.message;

  return typeof message === "string" && message.trim()
    ? message
    : "This field is required";
};

const UISkillsBox = ({
  label,
  name,
  required,
  rules,
  defaultValue,
  placeholder,
  disabled,
  maxWidth,
  option = [],
  source,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState("");
  const initializedFieldRef = useRef(null);
  const styles = useStyles();

  const form = Form.useFormInstance();
  const watchedSkills = Form.useWatch(name, form);

  const skillsValues = useMemo(() => {
    return dedupeSkills(normalizeSkills(watchedSkills));
  }, [watchedSkills]);

  useEffect(() => {
    const fieldKey = Array.isArray(name) ? name.join(".") : String(name);
    if (initializedFieldRef.current === fieldKey) return;

    const current = form.getFieldValue(name);

    if ((!current || current.length === 0) && option?.length > 0) {
      form.setFieldsValue({
        [name]: dedupeSkills(normalizeSkills(option)),
      });
    } else if ((!current || current.length === 0) && defaultValue && defaultValue !== 0) {
      form.setFieldsValue({
        [name]: dedupeSkills(normalizeSkills(defaultValue)),
      });
    }

    initializedFieldRef.current = fieldKey;
  }, [form, name, option, defaultValue]);

  const syncFormValue = (list) => {
    const normalizedList = dedupeSkills(normalizeSkills(list));
    form.setFieldsValue({
      [name]: normalizedList,
    });
    form.validateFields([name]).catch(() => {});
    onChange?.(normalizedList);
  };

  const getLocalOptions = (searchText) => {
    const text = searchText?.trim();
    if (!text) return [];

    const existing = skillsValues.map((item) => item?.Skill).filter(Boolean);

    const merged = dedupeSkills(
      [...existing, text].map((skill) => ({
        SkillId: skill,
        Skill: skill,
        SkillCode: skill,
        type: "custom",
      }))
    );

    return merged
      .filter((item) =>
        item?.Skill?.toLowerCase().includes(text.toLowerCase())
      )
      .map((item) => ({
        value: item.Skill,
        label: item.Skill,
      }));
  };

  const addSkill = (rawValue) => {
    const cleanValue = rawValue?.trim();
    if (!cleanValue) return;

    const alreadyExists = skillsValues.some(
      (item) => item?.Skill?.trim?.().toLowerCase() === cleanValue.toLowerCase()
    );

    if (alreadyExists) {
      setInputValue("");
      return;
    }

    const newItem = {
      SkillId: cleanValue,
      Skill: cleanValue,
      SkillCode: cleanValue,
      type: "custom",
    };

    syncFormValue([...skillsValues, newItem]);
    setInputValue("");
  };

  const onDelete = (item) => {
    const updated = skillsValues.filter(
      (skill) =>
        skill?.Skill?.trim?.().toLowerCase() !== item?.Skill?.trim?.().toLowerCase()
    );

    syncFormValue(updated);
  };

  return (
    <Form.Item shouldUpdate noStyle>
      {() => {
        const fieldErrors = form.getFieldError(name);
        const hasError = fieldErrors.length > 0;

        return (
          <Form.Item
            label={label}
            required={required}
            validateStatus={hasError ? "error" : ""}
            help={hasError ? fieldErrors[0] : null}
          >
            <>
              <Form.Item
                name={name}
                hidden
                rules={[
                  ...(required
                    ? [
                      {
                        validator: (_, value) => {
                          if (Array.isArray(value) && value.length > 0) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(getRequiredMessage(rules))
                          );
                        },
                      },
                    ]
                    : []),
                ]}
                initialValue={dedupeSkills(
                  normalizeSkills(
                    defaultValue && defaultValue !== 0 ? defaultValue : option
                  )
                )}
              >
                <input type="hidden" />
              </Form.Item>

              <div className={styles.wrapper}>
                <div className={styles.tagsWrap}>
                  {skillsValues.map((item, index) => (
                    <div key={`${item?.Skill}-${index}`} className={styles.tag}>
                      <span>{item?.Skill}</span>
                      {!disabled && (
                        <Icon
                          Icon={faX}
                          size={10}
                          color="gray"
                          style={{ marginLeft: 10, cursor: "pointer" }}
                          onClick={() => onDelete(item)}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {!disabled && (
                  <AutoComplete
                    status={hasError ? "error" : ""}
                    className={source === "JobPostForm" ? styles.inputJobPost : styles.input}
                    value={inputValue}
                    onChange={setInputValue}
                    onSearch={setInputValue}
                    onSelect={addSkill}
                    onBlur={() => addSkill(inputValue)}
                    placeholder={placeholder || "Type a skill and press enter to add"}
                    options={getLocalOptions(inputValue)}
                  />
                )}
              </div>
            </>
          </Form.Item>
        );
      }}
    </Form.Item>
  );
};

export default UISkillsBox;

const useStyles = createUseStyles({
  wrapper: {
    width: "100%",
  },
  input: {
    width: "100%",
    marginTop: 16
  },
  inputJobPost: {
    width: "100%",
    marginTop: 16,
    border: "1px solid #e5e7eb",
    borderRadius: 22,
    height: 42,
    display: "flex",
    alignItems: "center",
    boxShadow: "0 12px 35px rgba(15, 23, 42, 0.04)",
  },
  tagsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 6,
  },
  tag: {
        border: "1px solid #eee",
    // border: "1px solid #0c64ae",
    background: "#eff6ff",
    color: "#0c64ae",
    boxShadow: "0 10px 24px rgba(37, 99, 235, 0.05)",
    borderRadius: 16,
    padding: "6px 14px",
    display: "flex",
    alignItems: "center",
  },
});
